import {html} from '../ui/dom.js';

/**
 * The rules, in plain words, matching the paper. Everything stated here is
 * enforced by a contract with no owner, which is the only reason it is worth
 * stating.
 */
export function docsView(): string {
  return html`<section class="subpage docs">
    <div class="eyebrow">The rules</div>
    <h1>Four hours. One token. The whole pool.</h1>
    <p class="lede">Launchpads charge a fee on every trade and give it to the creator and the platform. The trader who
      pays it gets nothing. Here, part of every fee is pooled and spent buying one of the tokens back.</p>

    <div class="rules">
      <section>
        <h2><span class="number">01</span>Every trade pays 1.3%</h2>
        <p>On the ETH leg, on the curve and in the pool alike, so the fee is always in ETH and never in the token. It
          splits the moment it is paid: <b>0.5% to the token's creator</b>, <b>0.5% into the pool</b>, and
          <b>0.3% to the protocol</b>.</p>
      </section>

      <section>
        <h2><span class="number">02</span>The pool fills for four hours</h2>
        <p>Time is divided into four-hour periods, counted from the block of the first launch. Each period keeps a
          tally of what every token's trades paid into it. A token enters that period's draw once its trades have paid
          <b>0.05 ETH</b> of fees, which is about 3.85 ETH of volume. The threshold keeps tokens with negligible
          trading out of the list; it does not change any token's chance above it.</p>
      </section>

      <section>
        <h2><span class="number">03</span>One token is drawn, in proportion to what it paid</h2>
        <p>When the period closes, its tallies are final. A token's chance of being drawn is exactly its share of the
          fees paid. The whole pool is then spent buying that token, and <b>everything bought is burned</b>.</p>
        <p>The draw has two steps. Settling a period verifies the beacon and records the winner, and touches no
          market. The purchase is a separate step that anyone may run, in slices that each move the token's price by
          at most 10%, until the pool is spent. Whether a purchase can happen in a given block therefore has no
          bearing on who was drawn; a slice that cannot advance simply waits. The next period cannot settle until the
          purchase is complete.</p>
        <p>This is a buyback, not a payout. Nothing is distributed to holders; supply is destroyed and the ETH behind
          the remaining tokens goes up.</p>
      </section>

      <section>
        <h2><span class="number">04</span>Nobody picks the winner</h2>
        <p>The random value comes from <b>drand quicknet</b>, a beacon produced by a threshold of independent
          operators, which publishes a signature every three seconds. When a period closes, the contract fixes the
          round that will decide it: the one published about a minute later. The tallies are therefore final before
          any information about the outcome exists.</p>
        <p>The contract verifies that round's BLS12-381 signature itself, on chain, against the beacon's fixed group
          public key. A wrong signature fails; a signature for a different round fails. <b>Anyone may submit it</b> —
          there is no operator who can choose the winner, delay a draw they dislike, or withhold the pool.</p>
        <p>If the beacon ever stopped publishing, a period still unsettled seven days after its close could be
          settled from the hash of a block committed ten blocks ahead, again by anyone. The beacon path stays open
          meanwhile.</p>
      </section>

      <section>
        <h2><span class="number">05</span>Why manufacturing volume does not pay</h2>
        <p>A token's chance is its share of the fees, and the pool is those same fees, so the expected buyback each
          token receives is exactly the pool's share of its own volume: <b>0.5% of what it trades</b>, whoever trades
          it. Trading a token to raise its chance raises the pool by the same proportion.</p>
        <p>A creator who trades their own token pays 1.3% and gets 0.5% back through their ledger, a net cost of 0.8%,
          and raises their expected buyback by 0.5%. The expected return is <b>negative by the protocol's 0.3%</b>.
          The same holds for anyone else at the full 1.3%. The draw concentrates the buyback; it does not enlarge it.</p>
      </section>

      <section>
        <h2><span class="number">06</span>Launches carry no liquidity from their creator</h2>
        <p>A launch costs 0.0005 ETH and mints 1,000,000,000 tokens once, onto a bonding curve that trades against
          ETH. There is no mint function, no owner and no transfer restriction. The curve opens at 0.000000001 ETH and
          rises with every purchase.</p>
        <p>At <b>4 ETH raised</b> it has sold 800,000,000 and closes. That 4 ETH and 160,000,000 tokens become a single
          full-range Uniswap v4 position, at the price the curve closed at, held by a locker with no withdrawal
          function and no way to call arbitrary code. The remaining 40,000,000 are burned. The pool's own fee is the
          same 1.3%, and anyone may collect it into the current period.</p>
      </section>

      <section>
        <h2><span class="number">07</span>The launch window</h2>
        <p>For <b>three seconds</b> after a launch, a buy by anyone but the creator pays a 99% tax on top of the fee.
          It is what keeps bots from taking the opening price ahead of the people a token was launched for. After
          three seconds nothing is charged beyond the 1.3%. The trade panel shows the countdown and will tell you when
          you are inside it.</p>
      </section>

      <section>
        <h2><span class="number">08</span>What the operators can and cannot do</h2>
        <p>There is one privileged key, a multisignature wallet. It receives the protocol's 0.3% and it may stop the
          factory admitting new tokens. That is all it can do.</p>
        <p>It cannot alter, pause or withdraw from any curve, any pool, any creator's ledger, or the arena. None of
          those has an owner. If every service we run stopped tomorrow, every token would keep trading, every creator
          could still claim, anyone could still collect pool fees, and the arena would keep drawing whenever anyone
          submitted a beacon.</p>
        <p>There is no protocol token.</p>
      </section>

      <section>
        <h2><span class="number">09</span>What is disclosed rather than hidden</h2>
        <p>Between a round being published and the settlement landing on chain, the winner is computable by anyone who
          reads the beacon and the tallies, and once settled it is public. A trader can buy the winning token ahead of
          the purchase and sell into it. The slices cap what any one of them moves the price by, and what can be
          captured is a share of one period's pool.</p>
        <p>We disclose this rather than route the draw through a party who could keep the outcome private, because
          such a party could also decline to reveal an outcome it disliked. The draw history below shows the latency
          on every past draw.</p>
      </section>
    </div>

    <div class="docs-foot">
      <a class="secondary" href="https://github.com/99Zeyad/colosseum" target="_blank" rel="noopener">The contracts →</a>
      <a class="secondary" href="#/">Back to the arena</a>
    </div>
  </section>`;
}
